import Editor from '@monaco-editor/react';
import { EditorContainer } from '../../../style';
import { useTemplateEditorDispatch, useTemplateEditorState } from '../../../../context/UseTemplateEditorContext';
import { TemplateEditorCompilationSuggestionsModal } from '../../../modals/TemplateEditorCompilationSuggestionsModal';
import { isHandleBars, getOffsetPosition, getCaretPosition } from './utils';
import { FC, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useNotifications from 'core/notifications/useNotifications';
import { SiteEnrichmentList } from '../../../../../../../../client-management/ui/pages/client-settings/integrations/utils/enrichmentOptions';

type Theme = 'light' | 'vs-dark';

export interface NotificationProps {
  notificationTriggered: boolean;
  setNotificationTriggered: (triggered: boolean) => void;
}

export interface Props extends NotificationProps {
  model: any;
  viewState: any;
  onChange: (model: any, viewState: any) => void;
}

export const TemplateEditorView: FC<Props> = ({
  model,
  viewState,
  onChange,
  notificationTriggered,
  setNotificationTriggered,
}) => {
  const { template, editorSettings, site } = useTemplateEditorState();
  const dispatch = useTemplateEditorDispatch();
  const { showNotification } = useNotifications();
  const { t } = useTranslation(['templates']);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const caretOffset = useRef(0);
  const caretPosition = useRef({ left: 0, top: 0 });
  const monacoRef = useRef<any>(null);
  const enrichments = SiteEnrichmentList(site?.id);
  const hasTrendsEnrichment = enrichments?.some(enrich => enrich.value === 'Trends');

  const dispatchChange = (value: string) => {
    dispatch({
      type: 'SET_TEMPLATE_BODY',
      payload: {
        body: value,
      },
    });
  };

  const handleEditorChange = (event: any, value: string) => {
    const monacoModel = monacoRef.current.getModel();
    const monacoViewState = monacoRef.current.saveViewState();
    onChange(monacoModel, monacoViewState);
    suggestionModalActivator(event, value);
    dispatchChange(value);
  };

  const handleClick = (path: string) => {
    const newTemplate =
      template.template.slice(0, caretOffset.current + 1) + path + template.template.slice(caretOffset.current + 1);
    dispatchChange(newTemplate);
    setShowSuggestionModal(false);
  };

  const suggestionModalActivator = (e: any, v: string) => {
    if (!e || !e.changes || e.changes.length === 0) {
      setShowSuggestionModal(false);
      return;
    }

    caretOffset.current = getOffsetPosition(e.changes[0]);
    caretPosition.current = getCaretPosition(e.changes[0].range);

    if (hasTrendsEnrichment) {
      const trendsHandlebarMatch = /trendedItem|metric/.test(v);

      setShowSuggestionModal(isHandleBars(caretOffset.current, v));

      if (trendsHandlebarMatch && !notificationTriggered) {
        showNotification(t('lookbackPeriodNotification'), 'secondary', 0);
        setNotificationTriggered(true);
      } else if (!trendsHandlebarMatch) {
        setNotificationTriggered(false);
      }
    }
  };

  useEffect(() => {
    if (hasTrendsEnrichment) {
      const initialNotificationCheck = () => {
        const trendsHandlebarMatch = /trendedItem|metric/.test(template.template);
        if (trendsHandlebarMatch && !notificationTriggered) {
          showNotification(t('lookbackPeriodNotification'), 'secondary', 0);
          setNotificationTriggered(true);
        }
      };

      initialNotificationCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTrendsEnrichment]);

  const handleEditorDidMount = (editor: any, monaco: any) => {
    if (model != null && viewState != null) {
      editor.setModel(model);
      editor.restoreViewState(viewState);
      editor.setValue(template.template);
    }

    monacoRef.current = editor;
    monacoRef.current.focus();

    suggestionModalActivator(null, template.template);
  };

  return (
    <>
      <EditorContainer data-testid="EditorView">
        <Editor
          options={{
            minimap: { enabled: false },
            automaticLayout: true,
            wordWrap: editorSettings.wordWrap,
            multiCursorModifier: editorSettings.multiCursorModifier.control,
            readOnly: editorSettings.readOnly ?? false,
            hover: false,
          }}
          theme={editorSettings.theme as Theme}
          value={template.template}
          onChange={(value, ev) => {
            handleEditorChange(ev, value);
          }}
          saveViewState={true}
          keepCurrentModel={true}
          onMount={handleEditorDidMount}
          language={template?.channel === 'rcs' ? 'json' : 'html'}
          wrapperProps={{
            'aria-label': 'template editor',
            contenteditable: true,
          }}
        />
        <TemplateEditorCompilationSuggestionsModal
          show={showSuggestionModal}
          handleClick={handleClick}
          caretPosition={caretPosition}
        />
      </EditorContainer>
    </>
  );
};
